import { useCallback, useEffect, useRef, useState } from "react";
import type { ActivateAgentPaidAccessPayload } from "../../api/admin-agent-commerce";
import { t } from "../../i18n";

type PaidAccessForm = {
  readonly reason: string;
  readonly entitlement_path_ready: boolean;
};

const EMPTY_FORM: PaidAccessForm = {
  reason: "",
  entitlement_path_ready: false,
};

export function AgentPaidAccessActivationDialog({
  open,
  agentName,
  onConfirm,
  onCancel,
}: {
  readonly open: boolean;
  readonly agentName: string;
  readonly onConfirm: (payload: ActivateAgentPaidAccessPayload) => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [form, setForm] = useState<PaidAccessForm>(EMPTY_FORM);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const canConfirm = isValidForm(form);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  const close = useCallback((): void => {
    setForm(EMPTY_FORM);
    onCancel();
  }, [onCancel]);

  const submit = useCallback((): void => {
    if (!canConfirm) return;
    onConfirm({
      reason: form.reason.trim(),
      entitlement_path_ready: true,
    });
    setForm(EMPTY_FORM);
  }, [canConfirm, form.reason, onConfirm]);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="paid-access-title"
      style={styles.dialog}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
    >
      <div style={styles.content}>
        <h2 id="paid-access-title" style={styles.title}>
          {t("agent_paid_access.title")}
        </h2>
        <p style={styles.description}>
          {t("agent_paid_access.description", { name: agentName })}
        </p>
        <label htmlFor="paid-access-reason" style={styles.field}>
          <span style={styles.label}>{t("agent_paid_access.reason")}</span>
          <textarea
            id="paid-access-reason"
            rows={3}
            value={form.reason}
            onChange={(event) =>
              setForm({ ...form, reason: event.target.value })
            }
            style={styles.textarea}
          />
        </label>
        <label htmlFor="paid-access-entitlement-ready" style={styles.checkbox}>
          <input
            id="paid-access-entitlement-ready"
            type="checkbox"
            checked={form.entitlement_path_ready}
            onChange={(event) =>
              setForm({
                ...form,
                entitlement_path_ready: event.target.checked,
              })
            }
          />
          <span>{t("agent_paid_access.entitlement_path_ready")}</span>
        </label>
        <div style={styles.actions}>
          <button type="button" onClick={close} style={styles.cancelBtn}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            disabled={!canConfirm}
            onClick={submit}
            style={{
              ...styles.confirmBtn,
              ...(canConfirm ? undefined : styles.confirmBtnDisabled),
            }}
          >
            {t("agent_paid_access.confirm")}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function isValidForm(form: PaidAccessForm): boolean {
  return form.entitlement_path_ready && form.reason.trim().length >= 10;
}

const styles: Record<string, React.CSSProperties> = {
  dialog: {
    border: "none",
    borderRadius: 8,
    padding: 0,
    maxWidth: 520,
    width: "min(520px, calc(100vw - 32px))",
    boxShadow: "0 4px 24px rgba(0, 0, 0, 0.12)",
  },
  content: { padding: 24 },
  title: { fontSize: 18, fontWeight: 600, marginBottom: 8 },
  description: {
    fontSize: 14,
    color: "var(--color-text-secondary)",
    lineHeight: 1.6,
    marginBottom: 16,
  },
  field: { display: "grid", gap: 6, marginBottom: 12 },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
  },
  textarea: {
    width: "100%",
    padding: "8px 12px",
    border: "1px solid var(--color-border)",
    borderRadius: 6,
    resize: "vertical" as const,
    fontSize: 14,
    lineHeight: 1.5,
  },
  checkbox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "var(--color-text)",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 16,
  },
  cancelBtn: {
    padding: "8px 16px",
    fontSize: 14,
    borderRadius: 6,
    border: "1px solid var(--color-border)",
    background: "var(--color-bg)",
    color: "var(--color-text)",
  },
  confirmBtn: {
    padding: "8px 16px",
    fontSize: 14,
    fontWeight: 500,
    borderRadius: 6,
    border: "none",
    background: "var(--color-primary)",
    color: "#ffffff",
  },
  confirmBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
};
